import { useEffect, useRef } from 'react';
import { Popover } from 'antd';

/**
 * The popup of the formula editor's suggestions (KPR-332): what
 * `useFormulaSuggestions` decided, drawn. It is the combobox pattern for an
 * editable region — the editor keeps focus and the keyboard; the textbox names
 * the active row with `aria-activedescendant` (FormulaInput sets it), and a
 * polite status line says how many rows there are. The popup never takes
 * focus: a mouse press on a row is prevented from blurring the editor, and the
 * row is chosen on click.
 *
 * An antd Popover around the editor's frame: it opens in a portal, so a
 * dialog's edge never clips it, and flips above the field when there is no
 * room below. A function row shows its signature, what it does and an example;
 * an element row shows its words and the groups it sits under — never its
 * stored token (a person does not write ids in an ordinary setup). A value row
 * (a unit, a date part: KPR-350) shows its name and the spelling the formula
 * keeps (`kg`), under a heading for its group.
 */

function Row({ suggest, item, index }) {
	const rowRef = useRef(null);
	const selected = index === suggest.active;
	useEffect(() => {
		// The keyboard moved here: keep the row in the visible part of the list.
		if (selected && rowRef.current && typeof rowRef.current.scrollIntoView === 'function') {
			rowRef.current.scrollIntoView({ block: 'nearest' });
		}
	}, [selected]);
	const id = suggest.optionId(index);
	const describedBy = item.description ? `${id}-description` : undefined;
	return (
		<li
			ref={rowRef}
			id={id}
			role="option"
			aria-selected={selected}
			aria-labelledby={`${id}-label`}
			aria-describedby={describedBy}
			className={`formula-suggest__option${selected ? ' formula-suggest__option--active' : ''}`}
			onClick={() => suggest.accept(index)}
		>
			<span className="formula-suggest__head">
				<span id={`${id}-label`} className="formula-suggest__label">{item.label}</span>
				{item.detail ? <span className="formula-suggest__detail">{item.detail}</span> : null}
			</span>
			{item.description ? <span id={`${id}-description`} className="formula-suggest__description">{item.description}</span> : null}
			{item.example ? (
				<span className="formula-suggest__example">
					<span className="formula-suggest__example-label">Example</span>
					<code>{item.example}</code>
				</span>
			) : null}
		</li>
	);
}

/**
 * The rows, in runs of one `group` (KPR-350: units under their measure, date
 * parts under Calendar and Clock). Rows without a group stand alone.
 */
function runsOf(items) {
	const runs = [];
	items.forEach((item, index) => {
		const group = item.group || '';
		const last = runs[runs.length - 1];
		if (last && last.group === group) last.rows.push({ item, index });
		else runs.push({ group, rows: [{ item, index }] });
	});
	return runs;
}

function List({ suggest, ariaLabel }) {
	const rows = rowsOf => rowsOf.map(({ item, index }) => <Row key={item.key} suggest={suggest} item={item} index={index} />);
	return (
		// A press anywhere in the popup (its scrollbar too) must not blur the editor.
		<div className="formula-suggest" onMouseDown={event => event.preventDefault()}>
			<ul
				id={suggest.listId}
				role="listbox"
				aria-label={`Suggestions for ${ariaLabel}`}
				className="formula-suggest__list"
			>
				{runsOf(suggest.items).map((run, runIndex) => {
					if (!run.group) return rows(run.rows);
					const headingId = `${suggest.listId}-group-${runIndex}`;
					return (
						<li key={`group:${run.group}:${runIndex}`} role="group" aria-labelledby={headingId} className="formula-suggest__group">
							<div id={headingId} role="presentation" className="formula-suggest__group-heading">{run.group}</div>
							<ul role="none" className="formula-suggest__group-list">{rows(run.rows)}</ul>
						</li>
					);
				})}
			</ul>
			{suggest.more > 0 ? (
				<div className="formula-suggest__more">{suggest.more} more. Keep typing to narrow the list.</div>
			) : null}
		</div>
	);
}

/** The one line a screen reader hears when the list opens or changes. */
export function suggestionStatus(suggest) {
	if (!suggest.open) return '';
	const count = suggest.items.length;
	const rows = `${count} suggestion${count === 1 ? '' : 's'}${suggest.more > 0 ? ', more when you keep typing' : ''}`;
	return `${rows}. Up and down to review, Enter to choose, Escape to close.`;
}

export default function FormulaSuggestions({ suggest, ariaLabel, children }) {
	// While the popup fades out the list has already emptied: keep drawing the
	// last list it showed, or an empty frame flashes as it closes.
	const lastOpen = useRef(suggest);
	if (suggest.open) lastOpen.current = suggest;
	return (
		<Popover
			open={suggest.open}
			trigger={[]}
			placement="bottomLeft"
			arrow={false}
			// Slid into view, not flipped: on a phone the list is nearly as wide as
			// the screen and must not widen the page.
			autoAdjustOverflow={{ shiftX: 8, adjustY: true }}
			destroyOnHidden
			rootClassName="formula-suggest__popover"
			content={<List suggest={suggest.open ? suggest : lastOpen.current} ariaLabel={ariaLabel} />}
		>
			{children}
		</Popover>
	);
}
